import { useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";

import { updateMyInfo } from "src/api/user";
import LanguageSelect, { LANGUAGES, LanguageCode } from "src/components/LanguageSelect";
import useFeatureFlag, { FEATURE_FLAGS } from "src/hooks/useFeatureFlag";
import useUpdateUser from "src/hooks/useUpdateUser";
import useUser from "src/hooks/useUser";
import { applyActiveLanguage, normalizeLanguage } from "src/services/localization";
import { captureHandledException } from "src/services/sentry";

/**
 * The UI language of the dashboard, saved on the CO profile (`PUT /user/me`). Emails and
 * notifications use the IAM correspondence language instead, which is edited in the Cockpit profile.
 * Hidden while the "localization" flag is off: the app stays English then, so a choice would show nothing.
 */
export interface HeaderLanguageSelectProps {
  /**
   * `select` (default): a dropdown, for the desktop settings list.
   * `menu`: menu items for an MUI Menu (the mobile account menu). A dropdown inside `role="menu"` is
   * unreachable by arrow keys, so there it is one item that expands the four languages as items.
   */
  variant?: "select" | "menu";
}

const HeaderLanguageSelect = ({ variant = "select" }: HeaderLanguageSelectProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const user = useUser() as { language?: string } | null;
  const updateUser = useUpdateUser();
  const isLocalizationEnabled = useFeatureFlag(FEATURE_FLAGS.LOCALIZATION);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!isLocalizationEnabled) return null;

  const value = (normalizeLanguage(user?.language) ?? "en") as LanguageCode;

  const handleChange = async (language: LanguageCode) => {
    setSaving(true);
    try {
      await updateMyInfo({ language });
      updateUser({ language });
      applyActiveLanguage(language);
    } catch (error) {
      captureHandledException(error, { component: "HeaderLanguageSelect", action: "saveLanguage", language });
      enqueueSnackbar(t("header_settings.save_error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (variant === "menu") {
    const current = LANGUAGES.find((l) => l.code === value) ?? LANGUAGES[0];
    // An array, not a Fragment: MUI Menu rejects Fragment children.
    return [
      <MenuItem
        key="language"
        aria-expanded={expanded}
        disabled={saving}
        onClick={(e) => {
          // Keep the account menu open: this item only expands the language list.
          e.stopPropagation();
          setExpanded((open) => !open);
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {t("header_settings.language")}: <current.Flag /> {current.name}
        </Box>
      </MenuItem>,
      ...(expanded
        ? LANGUAGES.map(({ code, name, Flag }) => (
            <MenuItem
              key={code}
              role="menuitemradio"
              aria-checked={code === value}
              selected={code === value}
              sx={{ pl: 4 }}
              onClick={() => {
                setExpanded(false);
                if (code !== value) handleChange(code);
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Flag /> {name}
              </Box>
            </MenuItem>
          ))
        : []),
    ];
  }

  // A row like the list items around it: the label on the left, the current language on the right.
  // The menus around this close on any click or take arrow keys (MUI Menu) and on click-away
  // (the desktop list): keep the select's own events inside, and its options in the same DOM tree.
  // The header sets text-align: right, so the row resets it.
  return (
    <Box
      className="option-list-item header-language-select"
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 2, textAlign: "left" }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <ListItemText primary={t("header_settings.language")} sx={{ my: 0 }} />
      <LanguageSelect
        value={value}
        onChange={handleChange}
        label={t("header_settings.language")}
        disabled={saving}
        disablePortal
        variant="inline"
      />
    </Box>
  );
};

export default HeaderLanguageSelect;
